import React, { useState } from 'react';
import './FAQ.css';

export interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

export const faqs: FAQItem[] = [
  {
    category: 'General',
    question: 'What is JSON to TOON converter and how does it work?',
    answer: 'A JSON to TOON converter transforms standard JSON format into TOON (Token-Oriented Object Notation), a more efficient format that reduces token usage by 30-60%. It works by removing redundant syntax like braces, brackets, and unnecessary quotes while preserving data structure through indentation. All conversion happens locally in your browser using optimized JavaScript algorithms.'
  },
  {
    category: 'Savings',
    question: 'How much can I save by converting JSON to TOON?',
    answer: 'You can save 30-60% on token usage depending on your data structure. For example, with GPT-4 at $30 per million input tokens, converting 1 million JSON tokens saves $9-18 per request. Applications processing thousands of requests daily can reduce monthly API costs by 40-50%, potentially saving thousands of dollars.'
  },
  {
    category: 'Pricing',
    question: 'Is the JSON to TOON converter free?',
    answer: 'Yes, completely free with zero limitations. No signup, no subscription, no hidden fees. The tool runs entirely in your browser with no server costs, making it accessible to everyone.'
  },
  {
    category: 'Compatibility',
    question: 'Do LLMs understand TOON format natively?',
    answer: 'Yes, all major LLMs including GPT-4, Claude, Gemini, Llama, and others understand TOON format. Independent benchmarks show LLMs achieve 86.6% accuracy with TOON versus 83.2% with JSON. In many cases, TOON can actually improve model performance while reducing token costs.'
  },
  {
    category: 'Privacy',
    question: 'Is my data safe when using this converter?',
    answer: 'Absolutely. All conversion happens 100% locally in your browser. Your data never touches any server, making it completely safe for sensitive information including API keys, customer data, and proprietary information. We don\'t collect, store, or transmit any of your data.'
  },
  {
    category: 'Performance',
    question: 'Can I convert large JSON files?',
    answer: 'Yes, the web converter supports JSON files up to 10MB with efficient handling of complex nested structures and large arrays. Perfect for most use cases including large API responses, database exports, and configuration files.'
  },
  {
    category: 'Format',
    question: 'What\'s the difference between TOON and JSON?',
    answer: 'TOON removes redundant syntax (braces, brackets, quotes) and uses indentation like YAML for structure. It also optimizes arrays of objects by declaring fields as headers rather than repeating them. This achieves 30-60% token reduction while maintaining perfect data fidelity and human readability.'
  },
  {
    category: 'Data Integrity',
    question: 'Will converting to TOON change my data?',
    answer: 'No, TOON conversion is completely lossless. Only the format changes—all values, data types, null values, nested structures, and relationships remain exactly the same. You can convert JSON to TOON and back to JSON without any information loss or data corruption.'
  },
  {
    category: 'Providers',
    question: 'Which LLM providers work with TOON?',
    answer: 'TOON works with all major providers: OpenAI (GPT-3.5, GPT-4, GPT-4o), Anthropic (Claude 3, Claude Sonnet), Google (Gemini Pro, Gemini Ultra), Cohere, Meta (Llama), Mistral, and all open-source models. Token savings are consistent across all providers.'
  },
  {
    category: 'Use Cases',
    question: 'What are the best use cases for TOON?',
    answer: 'TOON excels in RAG systems (reduce embeddings and context), API integration (compress responses), chatbots (optimize conversation history), data analysis (fit more data in prompts), code generation (include larger configs), and any scenario where you\'re sending structured data to LLMs.'
  },
  {
    category: 'Technical',
    question: 'Does TOON support all JSON features?',
    answer: 'Yes, TOON supports all JSON features including nested objects, arrays, primitives (strings, numbers, booleans), null values, unicode characters, escape sequences, and deeply nested structures. If it\'s valid JSON, it can be converted to TOON.'
  },
  {
    category: 'Comparison',
    question: 'How does TOON compare to YAML or other formats?',
    answer: 'TOON is specifically optimized for LLM token efficiency, achieving better compression than YAML. Unlike YAML, TOON is designed for programmatic generation and parsing, with simpler rules and better performance. It\'s also more token-efficient than Protocol Buffers or MessagePack for LLM contexts.'
  },
  {
    category: 'Support',
    question: 'Can I convert TOON back to JSON?',
    answer: 'Yes, TOON is bidirectional. You can convert JSON to TOON and back to JSON without any data loss. The converter provides both directions with perfect fidelity.'
  },
  {
    category: 'Performance',
    question: 'How fast is the conversion?',
    answer: 'Extremely fast. The converter uses zero external dependencies and optimized algorithms for instant conversion, even with large datasets. Most conversions complete in milliseconds. The web interface provides real-time conversion as you type.'
  }
];

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const categories = Array.from(new Set(faqs.map(faq => faq.category)));

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="faq-section" id="faq" aria-labelledby="faq-heading">
      <div className="faq-container">
        <header className="faq-header">
          <h2 id="faq-heading" className="faq-title">
            Frequently Asked Questions
          </h2>
          <p className="faq-subtitle">
            Everything you need to know about JSON to TOON conversion
          </p>
        </header>

        <div className="faq-categories">
          {categories.map((category) => (
            <span key={category} className="category-badge">
              {category}
            </span>
          ))}
        </div>

        <div className="faq-list">
          {faqs.map((faq, index) => (
            <article
              key={index}
              className={`faq-item ${openIndex === index ? 'active' : ''}`}
              itemScope
              itemType="https://schema.org/Question"
            >
              <button
                className="faq-question"
                onClick={() => toggleFAQ(index)}
                aria-expanded={openIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                <span className="question-category">[{faq.category}]</span>
                <span className="question-text" itemProp="name">{faq.question}</span>
                <span className="question-icon" aria-hidden="true">
                  {openIndex === index ? '−' : '+'}
                </span>
              </button>
              <div
                id={`faq-answer-${index}`}
                className="faq-answer"
                role="region"
                aria-labelledby={`faq-question-${index}`}
                itemScope
                itemType="https://schema.org/Answer"
                itemProp="acceptedAnswer"
              >
                <p itemProp="text">{faq.answer}</p>
              </div>
            </article>
          ))}
        </div>

        <div className="faq-cta">
          <h3>Still have questions?</h3>
          <p>
            Try the converter yourself and see the token savings in real-time. It's free, fast, and requires no signup.
          </p>
          <a href="#converter" className="cta-button">
            Start Converting Now
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
